import { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  Fish,
  Film,
  Layers,
  Loader2,
  Pencil,
  Play,
  Plus,
  AlertCircle,
  Sparkles,
  Trash2,
} from 'lucide-react'
import type { Composition } from '@shared/types'
import { api } from '../../api'
import { useEditorStore } from '../../store/editorStore'
import { Button } from '../common/Button'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { Menu, MenuItem, MenuPanel } from '../common/Menu'

const SAVE_LABEL: Record<string, { text: string; icon: React.ReactNode; cls: string }> = {
  idle: { text: '未保存', icon: null, cls: 'text-text-faint' },
  saving: { text: '保存中…', icon: <Loader2 size={12} className="animate-spin" />, cls: 'text-text-muted' },
  saved: { text: '保存済み', icon: <Check size={12} />, cls: 'text-accent-blue' },
  error: { text: '保存エラー', icon: <AlertCircle size={12} />, cls: 'text-accent-red' },
}

/**
 * P10-2 (docs/P10_PHASE1.md §3 item 9): "進行中jobがあれば削除をdisabledにし
 * 理由をツールチップ(storeのjob状態を使う。無ければlistJobsで判定)". The store
 * already tracks `previewJob`/`renderJob` for whatever this session itself
 * kicked off (instant, no request) — checked first; `GET /api/jobs` is the
 * fallback that also catches a Job started before this page load / from
 * another tab, which the store alone can't know about. Re-checked whenever
 * the open Composition changes or either store Job's status changes.
 */
function useCompositionBusy(compositionId: string | undefined): { busy: boolean; reason: string | null } {
  const previewJob = useEditorStore((s) => s.previewJob)
  const renderJob = useEditorStore((s) => s.renderJob)
  const [remoteBusy, setRemoteBusy] = useState(false)
  const storeBusy =
    previewJob?.status === 'queued' ||
    previewJob?.status === 'running' ||
    renderJob?.status === 'queued' ||
    renderJob?.status === 'running'

  useEffect(() => {
    if (!compositionId) {
      setRemoteBusy(false)
      return
    }
    let cancelled = false
    api
      .listJobs({ compositionId })
      .then((jobs) => {
        if (!cancelled) setRemoteBusy(jobs.some((j) => j.status === 'queued' || j.status === 'running'))
      })
      .catch(() => {
        if (!cancelled) setRemoteBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [compositionId, previewJob?.status, renderJob?.status])

  const busy = storeBusy || remoteBusy
  return { busy, reason: busy ? '書き出し／プレビュー生成が進行中のため削除できません' : null }
}

/** Inline rename for the currently open Composition — same Enter/Esc/empty-rejected convention as ProjectList's RenameInput. */
function CompositionRenameInput({ composition, onDone }: { composition: Composition; onDone: () => void }) {
  const renameComposition = useEditorStore((s) => s.renameComposition)
  const [value, setValue] = useState(composition.name)

  function commit() {
    const trimmed = value.trim()
    if (trimmed && trimmed !== composition.name) renameComposition(trimmed)
    onDone()
  }

  return (
    <input
      autoFocus
      data-testid="composition-rename-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onDone()
        }
      }}
      className="w-40 rounded bg-ink-bg/70 border border-accent-blue/60 px-1.5 py-0.5 text-sm text-text focus:outline-none"
    />
  )
}

export function Header() {
  const {
    currentProject,
    composition,
    saveState,
    backToProjects,
    openProject,
    requestPreview,
    requestRender,
    previewJob,
    renderJob,
    deleteComposition,
    duplicateComposition,
    createComposition,
  } = useEditorStore()
  const save = SAVE_LABEL[saveState]
  const previewBusy = previewJob?.status === 'queued' || previewJob?.status === 'running'
  const renderBusy = renderJob?.status === 'queued' || renderJob?.status === 'running'
  // docs/COMPOSER_CONTRACT.md §6.1 "「コンパネ」バッジの最小追加" — shown
  // whenever the open Composition was generated from the AA console
  // ("構成案コンパネ"), so opening a ComposerDraft.studioUrl link makes its
  // origin visible immediately.
  const composerSource = composition?.meta.source

  const [siblingCompositions, setSiblingCompositions] = useState<Composition[]>([])
  const [renamingComposition, setRenamingComposition] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const { busy: deleteBusy, reason: deleteBusyReason } = useCompositionBusy(composition?.id)

  const compositionIdsKey = currentProject?.compositionIds.join(',') ?? ''
  useEffect(() => {
    if (!currentProject) {
      setSiblingCompositions([])
      return
    }
    let cancelled = false
    api
      .getProject(currentProject.id)
      .then((detail) => {
        if (!cancelled) {
          setSiblingCompositions([...detail.compositions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)))
        }
      })
      .catch(() => {
        if (!cancelled) setSiblingCompositions([])
      })
    return () => {
      cancelled = true
    }
    // `composition?.id` (reviewer 7/10 差し戻し B-1/m3 の組み合わせ): refetch
    // on every SWITCH too, not only when the id set itself changes. B-1's
    // fix makes `openProject` flush the outgoing Composition's pending
    // rename/save to the server before switching — but that only reaches
    // THIS component's own `siblingCompositions` cache via a fresh fetch;
    // without this dep, switching away from a just-renamed Composition and
    // back would still show its PRE-rename name (the render-time merge
    // below only patches the entry matching the CURRENTLY open id, not one
    // just switched away from). `composition?.id` only changes on a real
    // switch (autoEdit/mutate/renameComposition all keep the same id), so
    // this doesn't add any refetch on ordinary edits/autosave ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, compositionIdsKey, composition?.id])

  // m3 (reviewer 7/10 差し戻し): the effect above only refetches when
  // `compositionIds` itself changes (create/duplicate/delete) — renaming or
  // otherwise saving the CURRENTLY open Composition doesn't touch that array,
  // so the dropdown kept showing the pre-rename name/stale order until the
  // next unrelated refetch. Reconciled here at render time instead of
  // widening the effect's deps (which would mean an extra `getProject`
  // round-trip on every autosave tick): the entry matching `composition.id`
  // always takes its name/updatedAt from the live `composition` object —
  // the one source of truth for what's actually open right now — then the
  // list is re-sorted so its position (updatedAt desc) stays current too.
  const displayCompositions = composition
    ? siblingCompositions
        .map((c) => (c.id === composition.id ? { ...c, name: composition.name, updatedAt: composition.updatedAt } : c))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    : siblingCompositions

  async function handleDuplicate() {
    if (!composition) return
    await duplicateComposition(composition.id)
  }

  async function handleCreateNew() {
    await createComposition('無題の構成案')
  }

  async function handleConfirmDelete() {
    if (!currentProject || !composition) return
    const deletedId = composition.id
    await deleteComposition(deletedId)
    // task spec §3-9: 削除で現在の構成案を消したら残りの先頭を開き、残り0なら
    // openProject の既存経路で「初稿」が自動生成される — deleteComposition
    // already dropped `deletedId` from currentProject.compositionIds.
    const remaining = useEditorStore.getState().currentProject?.compositionIds ?? []
    await openProject(currentProject.id, remaining[0])
    setConfirmingDelete(false)
  }

  return (
    <header className="flex items-center gap-4 px-4 h-14 border-b border-ink-border bg-ink-surface/80 backdrop-blur-glass shrink-0">
      <button
        onClick={() => void backToProjects()}
        className="flex items-center gap-1.5 text-text-muted hover:text-text text-xs"
        title="プロジェクト一覧へ"
        data-testid="header-back-to-projects"
      >
        <ArrowLeft size={14} />
      </button>
      <div className="flex items-center gap-2">
        <Fish size={18} className="text-accent-red" strokeWidth={2.2} />
        <span className="font-bold text-sm tracking-wide">AQUACUT</span>
      </div>
      <div className="w-px h-5 bg-ink-border" />
      <div className="text-sm font-medium truncate max-w-[160px]" data-testid="project-name" title={currentProject?.name}>
        {currentProject?.name ?? '（無題）'}
      </div>
      <div className="w-px h-5 bg-ink-border" />

      {renamingComposition && composition ? (
        <CompositionRenameInput composition={composition} onDone={() => setRenamingComposition(false)} />
      ) : (
        <Menu
          testId="composition-switcher"
          align="start"
          trigger={({ toggle }) => (
            <button
              type="button"
              onClick={toggle}
              className="flex items-center gap-1.5 text-sm font-medium truncate max-w-[220px] text-text hover:text-accent-blue"
              data-testid="composition-switcher-trigger"
              title={composition?.name}
            >
              <Layers size={13} className="text-text-faint shrink-0" />
              <span className="truncate">{composition?.name ?? '（無題）'}</span>
              <ChevronDown size={12} className="text-text-faint shrink-0" />
            </button>
          )}
        >
          {({ close }) => (
            <MenuPanel className="w-72">
              <div className="max-h-56 overflow-y-auto py-1" data-testid="composition-switcher-list">
                {displayCompositions.map((c) => {
                  const isCurrent = c.id === composition?.id
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid="composition-switcher-item"
                      data-composition-id={c.id}
                      onClick={() => {
                        close()
                        if (!isCurrent && currentProject) void openProject(currentProject.id, c.id)
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-white/5 ${
                        isCurrent ? 'text-accent-blue' : 'text-text'
                      }`}
                    >
                      {isCurrent ? (
                        <Check size={12} className="shrink-0" data-testid="composition-switcher-current-check" />
                      ) : (
                        <span className="w-3 shrink-0" />
                      )}
                      <span className="truncate flex-1">{c.name}</span>
                      {c.meta.source && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-accent-blue/15 text-accent-blue shrink-0">
                          コンパネ
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              <div className="border-t border-ink-border my-1" />
              <MenuItem
                icon={<Plus size={13} />}
                testId="composition-switcher-new"
                onSelect={() => {
                  close()
                  void handleCreateNew()
                }}
              >
                新規（空）
              </MenuItem>
              <MenuItem
                icon={<Copy size={13} />}
                testId="composition-switcher-duplicate"
                onSelect={() => {
                  close()
                  void handleDuplicate()
                }}
              >
                複製
              </MenuItem>
              <MenuItem
                icon={<Pencil size={13} />}
                testId="composition-switcher-rename"
                onSelect={() => {
                  close()
                  setRenamingComposition(true)
                }}
              >
                名前を変更
              </MenuItem>
              <MenuItem
                icon={<Trash2 size={13} />}
                danger
                disabled={deleteBusy}
                title={deleteBusyReason ?? undefined}
                testId="composition-switcher-delete"
                onSelect={() => {
                  close()
                  setConfirmingDelete(true)
                }}
              >
                削除
              </MenuItem>
            </MenuPanel>
          )}
        </Menu>
      )}

      <div className={`flex items-center gap-1 text-[11px] ${save.cls}`} data-testid="save-state">
        {save.icon}
        {save.text}
      </div>
      {composerSource && (
        <div
          className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-accent-blue/40 text-accent-blue bg-accent-blue/10"
          data-testid="composer-badge"
          title={`specId: ${composerSource.specId} / batchId: ${composerSource.batchId}`}
        >
          <Sparkles size={11} />
          コンパネ · {composerSource.pattern} · seed {composerSource.seed}
        </div>
      )}

      <div className="flex-1" />

      <Button
        variant="secondary"
        size="sm"
        icon={previewBusy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        onClick={requestPreview}
        disabled={previewBusy}
        data-testid="btn-preview"
      >
        プレビュー生成
      </Button>
      <Button
        variant="primary"
        size="sm"
        icon={renderBusy ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
        onClick={requestRender}
        disabled={renderBusy}
        data-testid="btn-export"
      >
        書き出し
      </Button>

      {confirmingDelete && composition && (
        <ConfirmDialog
          title={`『${composition.name}』を削除しますか？`}
          message="この構成案のカット割り・テロップ・書き出し履歴が削除されます。元に戻せません。"
          busyMessage="書き出し／プレビュー生成中のため削除できません"
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </header>
  )
}
